import { makeVar } from '@apollo/client';
import { createContext, useBoolean } from '@hover/blueprint';
import type { MotionValue } from 'framer-motion';
import { useMotionValue } from 'framer-motion';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { useMemoContextValue } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { preloadImage } from '../../helpers';
import { useHomeownerAppContext } from '../../hooks';
import type { AiOriginalOrGeneratedImage } from '../../types';
import {
  getImagePath,
  isAiGenerated,
  isImageEqual,
  isOriginal,
} from '../../types';

import type { Direction, Tracking } from './types';
import type { ImagePair } from './useImagePair';

export type GenerateNextImage = () => void;

export interface FullscreenImageContentsValue {
  image: AiDisplayImageFragment;
  images?: AiDisplayImageFragment[];
  /**
   * Base route for the current fullscreen image, this is used to generate the
   * paths for the next and previous image when paging through images.
   *
   * @remarks the path must include `:aiDisplayImageId` as the only route
   * parameter. Any additional parameters must be interpolated into the path
   * string beforehand.
   */
  path: string;
  imagePair: ImagePair;
  tracking: Tracking;
  generateNextImage?: GenerateNextImage;
  onLayoutAnimationComplete: () => void;
  isLayoutAnimationComplete: boolean;
  disableCarousel?: boolean;
}

export interface FullscreenImageContentsContext
  extends FullscreenImageContentsValue {
  tracking: Tracking;
  direction: Direction;
  next: () => void;
  previous: () => void;
  nextImage: AiDisplayImageFragment | null;
  previousImage: AiDisplayImageFragment | null;
  carouselMotion: MotionValue<number>;
  isShowingOriginalImage: boolean;
  toggleOriginalImage: () => void;
}

export const [Provider, useContext] =
  createContext<FullscreenImageContentsContext>({ strict: true });

/**
 * This reactive variable tracks the current fullscreen image until it has
 * animated back to its position upon exiting fullscreen. This is necessary in
 * addition to `useIsFullscreenImageOpen` and `useActiveFullscreenImage`, etc.
 * to manage some things that need to happen as the fullscreen thumbnail
 * animates back from fullscreen to its initial position.
 */
export const latestFullscreenImage = makeVar<AiOriginalOrGeneratedImage | null>(
  null,
);

export interface FullscreenImageContentsProviderProps {
  children: ReactNode;
  value: FullscreenImageContentsValue;
}

type GetIndexOptions = Pick<FullscreenImageContentsValue, 'image' | 'images'>;

const getIndex = ({ image, images }: GetIndexOptions) => {
  if (isOriginal(image)) return null;

  return images?.findIndex(i => isImageEqual(i, image)) ?? null;
};

const getImage = (
  index: number | null,
  { images }: FullscreenImageContentsValue,
) => {
  if (index === null || !images) return null;

  return images[index] ?? null;
};

const getAdjacent = (value: FullscreenImageContentsValue) => {
  const currentIndex = getIndex(value);
  const foundIndex = currentIndex !== null;

  const previousIndex = foundIndex ? currentIndex - 1 : null;
  const nextIndex = foundIndex ? currentIndex + 1 : null;
  const onDeckIndex = foundIndex ? currentIndex + 2 : null;

  return {
    currentIndex,
    previousIndex,
    nextIndex,
    onDeckIndex,
    nextImage: !value.disableCarousel ? getImage(nextIndex, value) : null,
    previousImage: !value.disableCarousel
      ? getImage(previousIndex, value)
      : null,
    onDeckImage: !value.disableCarousel ? getImage(onDeckIndex, value) : null,
  };
};

const getCurrent = (value: FullscreenImageContentsValue) => {
  const i = getIndex(value);

  return i === null ? null : value.images?.[i];
};

/**
 * Derive the direction of pagination from
 * the current and previous image indices
 */
const getDirection = (
  { image, images }: GetIndexOptions,
  priorImage: AiDisplayImageFragment,
) => {
  const currentIndex = getIndex({ image, images });
  const previousIndex = getIndex({ image: priorImage, images });

  if (!previousIndex || !currentIndex)
    return currentIndex === 0 ? 'previous' : 'next';

  return currentIndex > previousIndex ? 'next' : 'previous';
};

/**
 * Given the current fullscreen contents context state, start generating up to
 * the next two images when reaching the end of the generated image list.
 *
 * @param value fullscreen image contents context value
 */
const useGenerateUpcomingImages = (value: FullscreenImageContentsValue) => {
  // Because newly generated images aren't added to the list until the
  // placeholder record comes back, we need to track which images we've started
  // generating to prevent firing multiple generation requests before the
  // "generating" placeholder records are added to the `images` state.
  const started = useRef(new Set<number>());

  const { image, generateNextImage, isLayoutAnimationComplete } = value;
  const { currentIndex, nextIndex, nextImage, onDeckIndex, onDeckImage } =
    getAdjacent(value);

  const generatedImage = image.aiGeneratedImage;

  // Once we have actual image records, update our local generation tracking. We
  // use the array index to ensure the state "follows" the image position that
  // initiated the generation as the user advances to the next image.
  if (currentIndex) started.current.delete(currentIndex);
  if (nextImage && nextIndex) started.current.delete(nextIndex);
  if (onDeckImage && onDeckIndex) started.current.delete(onDeckIndex);

  const canGenerateImages =
    isAiGenerated(generatedImage) &&
    !!generateNextImage &&
    isLayoutAnimationComplete;

  useEffect(() => {
    if (!canGenerateImages) return;

    if (!nextImage && nextIndex && !started.current.has(nextIndex)) {
      started.current.add(nextIndex);
      generateNextImage();
    }
  }, [canGenerateImages, generateNextImage, nextImage, nextIndex]);

  useEffect(() => {
    if (!canGenerateImages) return;

    if (!onDeckImage && onDeckIndex && !started.current.has(onDeckIndex)) {
      started.current.add(onDeckIndex);
      generateNextImage();
    }
  }, [canGenerateImages, generateNextImage, onDeckImage, onDeckIndex]);
};

const FullscreenImageContentsProvider = ({
  children,
  value,
}: FullscreenImageContentsProviderProps) => {
  const { image: valueImage, path } = value;

  const image = getCurrent(value) ?? valueImage;

  const [priorImage, setPriorImage] = useState(image);
  const [direction, setDirection] = useState<Direction>('next');
  const { trackingData } = useHomeownerAppContext();

  const [
    isShowingOriginalImage,
    { toggle: toggleOriginalImage, off: hideOriginalImage },
  ] = useBoolean(false);

  const carouselMotion = useMotionValue(0);

  if (image.id !== priorImage.id) {
    hideOriginalImage();
    setDirection(getDirection(value, priorImage));
    setPriorImage(image);
  }

  const history = useHistory();
  const { nextImage, previousImage, onDeckImage } = getAdjacent(value);

  const next = useCallback(() => {
    if (!nextImage) return;

    const generatedImage = nextImage.aiGeneratedImage;
    latestFullscreenImage(generatedImage);

    buttonPressed({
      ...trackingData,
      ai_generated_image_id: isAiGenerated(generatedImage)
        ? generatedImage.id
        : undefined,
      ai_original_image_id: isAiGenerated(generatedImage)
        ? generatedImage.aiOriginalImageId
        : undefined,
      button_text: 'next',
      track_id: 'generated-full-screen',
      feature: 'homeowner-web-app',
      space_type: nextImage.spaceType || undefined,
    });

    history.push(generatePath(path, { aiDisplayImageId: nextImage.id }));
  }, [nextImage, trackingData, history, path]);

  const previous = useCallback(() => {
    if (!previousImage) return;
    const generatedImage = previousImage.aiGeneratedImage;
    latestFullscreenImage(generatedImage);

    buttonPressed({
      ...trackingData,
      ai_generated_image_id: isAiGenerated(generatedImage)
        ? generatedImage.id
        : undefined,
      ai_original_image_id: isAiGenerated(generatedImage)
        ? generatedImage.aiOriginalImageId
        : undefined,
      button_text: 'previous',
      track_id: 'generated-full-screen',
      feature: 'homeowner-web-app',
      space_type: previousImage.spaceType || undefined,
    });

    history.push(generatePath(path, { aiDisplayImageId: previousImage.id }));
  }, [history, path, previousImage, trackingData]);

  useEffect(() => {
    if (onDeckImage) void preloadImage(getImagePath(onDeckImage));
  }, [onDeckImage]);

  useGenerateUpcomingImages(value);

  return (
    <Provider
      value={useMemoContextValue({
        ...value,
        carouselMotion,
        direction,
        next,
        previous,
        image,
        nextImage,
        previousImage,
        isShowingOriginalImage,
        toggleOriginalImage,
      })}
    >
      {children}
    </Provider>
  );
};

export {
  FullscreenImageContentsProvider,
  useContext as useFullscreenImageContents,
};
